import React from 'react';
import { StanceSettings, SILHOUETTES, VehicleSilhouetteId } from '../../types/vehicle';
import { calculatePitchAndTransforms } from '../../utils/canvasRenderer';
import { Minus, Plus, Compass } from 'lucide-react';

export interface SuspensionRackProps {
  stance: StanceSettings;
  silhouetteId: VehicleSilhouetteId;
  onChangeStance: (updates: Partial<StanceSettings>) => void;
}

export const SuspensionRack: React.FC<SuspensionRackProps> = ({
  stance,
  silhouetteId,
  onChangeStance,
}) => {
  const silKey = silhouetteId === 'custom_upload' ? 'classic_muscle' : silhouetteId;
  const silhouette = SILHOUETTES[silKey] || SILHOUETTES.classic_muscle;
  const wheelbasePx = silhouette.rearHubX - silhouette.frontHubX;

  const kinematics = calculatePitchAndTransforms({
    wheelbase: wheelbasePx,
    frontDrop: stance.frontHeightOffsetInches,
    rearDrop: stance.rearHeightOffsetInches,
    scale: silhouette.scalePxPerInch,
  });

  const nudge = (field: 'frontHeightOffsetInches' | 'rearHeightOffsetInches', delta: number) => {
    const current = stance[field];
    const next = Math.max(-4.0, Math.min(2.0, Math.round((current + delta) * 100) / 100));
    onChangeStance({ [field]: next });
  };

  const quickPresets = [
    { label: 'Slammed Track', front: -3.5, rear: -3.0, desc: 'Aggressive pro-touring tuck' },
    { label: 'Detroit Street', front: -2.0, rear: -0.5, desc: 'Classic forward rake muscle' },
    { label: 'Factory Stock', front: 0.0, rear: 0.0, desc: 'OEM showroom ride height' },
    { label: 'Gasser / Drag', front: 1.5, rear: -1.0, desc: 'High nose gasser weight transfer' },
    { label: 'Bagged Grounded', front: -4.0, rear: -4.0, desc: 'Frame on the ground' },
  ];

  const getStanceCategory = (drop: number) => {
    if (drop <= -3.0) return 'PRO-TOURING SLAM';
    if (drop < -1.0) return 'STREET DROP';
    if (drop < 0.5) return 'STOCK LEVEL';
    return 'LIFTED GASSER';
  };

  return (
    <div className="space-y-6">
      {/* 1. Real-time Numerical Stance & Kinematics Gauge Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <div className="bg-shop-darkest p-3 rounded border border-shop-border space-y-1">
          <span className="text-[10px] font-mono text-shop-muted uppercase block">
            FRONT DROP
          </span>
          <div className="text-xl font-bold font-mono text-safety-amber">
            {stance.frontHeightOffsetInches >= 0 ? '+' : ''}
            {stance.frontHeightOffsetInches.toFixed(2)}&quot;
          </div>
          <span className="text-[10px] font-mono text-shop-light block">
            Gap: {kinematics.fenderClearanceFront.toFixed(2)}&quot;{' '}
            {kinematics.fenderClearanceFront < 0 && (
              <strong className="text-safety-red">[TUCKED]</strong>
            )}
          </span>
        </div>

        <div className="bg-shop-darkest p-3 rounded border border-shop-border space-y-1">
          <span className="text-[10px] font-mono text-shop-muted uppercase block">
            REAR DROP
          </span>
          <div className="text-xl font-bold font-mono text-safety-amber">
            {stance.rearHeightOffsetInches >= 0 ? '+' : ''}
            {stance.rearHeightOffsetInches.toFixed(2)}&quot;
          </div>
          <span className="text-[10px] font-mono text-shop-light block">
            Gap: {kinematics.fenderClearanceRear.toFixed(2)}&quot;{' '}
            {kinematics.fenderClearanceRear < 0 && (
              <strong className="text-safety-red">[TUCKED]</strong>
            )}
          </span>
        </div>

        <div className="bg-shop-darkest p-3 rounded border border-shop-border space-y-1">
          <span className="text-[10px] font-mono text-shop-muted uppercase block">
            STANCE RAKE DELTA
          </span>
          <div className="text-xl font-bold font-mono text-safety-cyan">
            {kinematics.rakeInches >= 0 ? '+' : ''}
            {kinematics.rakeInches.toFixed(2)}&quot;
          </div>
          <span className="text-[10px] font-mono text-shop-muted block">
            {kinematics.rakeInches > 0
              ? 'Nose-Down'
              : kinematics.rakeInches < 0
              ? 'Nose-Up Squat'
              : 'Level Stance'}
          </span>
        </div>

        <div className="bg-shop-darkest p-3 rounded border border-shop-border space-y-1">
          <span className="text-[10px] font-mono text-shop-muted uppercase block">
            RAKE PITCH ANGLE
          </span>
          <div className="text-xl font-bold font-mono text-safety-cyan flex items-center gap-1">
            <Compass className="w-4 h-4 text-safety-cyan shrink-0" />
            <span>
              {kinematics.pitchAngleDeg >= 0 ? '+' : ''}
              {kinematics.pitchAngleDeg.toFixed(2)}°
            </span>
          </div>
          <span className="text-[10px] font-mono text-shop-muted block">
            {kinematics.pitchAngleDeg > 0.05
              ? 'Aggressive Forward'
              : kinematics.pitchAngleDeg < -0.05
              ? 'Gasser Stance'
              : 'Neutral Level'}
          </span>
        </div>
      </div>

      {/* 2. Interactive Front & Rear Height Sliders with Nudge Buttons */}
      <div className="space-y-5 p-4 bg-shop-darkest border border-shop-border rounded">
        {/* Front Ride Height */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-mono">
            <div className="flex items-center gap-2">
              <span className="font-bold uppercase tracking-wider text-shop-highlight">
                Front Suspension Height
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-shop-panel text-shop-muted border border-shop-border">
                {getStanceCategory(stance.frontHeightOffsetInches)}
              </span>
            </div>
            <span className="text-safety-amber font-bold">
              {stance.frontHeightOffsetInches >= 0 ? '+' : ''}
              {stance.frontHeightOffsetInches.toFixed(2)}&quot;
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => nudge('frontHeightOffsetInches', -0.25)}
              className="min-h-[44px] min-w-[44px] rounded bg-shop-panel hover:bg-shop-well text-shop-highlight border border-shop-border flex items-center justify-center cursor-pointer active:translate-y-0.5 transition-all duration-100"
              title="Lower front 0.25 inches"
            >
              <Minus className="w-4 h-4" />
            </button>

            <div className="flex-1 relative flex items-center h-11">
              <input
                type="range"
                min="-4.0"
                max="2.0"
                step="0.25"
                value={stance.frontHeightOffsetInches}
                onChange={(e) =>
                  onChangeStance({
                    frontHeightOffsetInches: parseFloat(e.target.value),
                  })
                }
                className="w-full accent-safety-amber h-2 bg-shop-panel rounded cursor-pointer"
              />
            </div>

            <button
              onClick={() => nudge('frontHeightOffsetInches', 0.25)}
              className="min-h-[44px] min-w-[44px] rounded bg-shop-panel hover:bg-shop-well text-shop-highlight border border-shop-border flex items-center justify-center cursor-pointer active:translate-y-0.5 transition-all duration-100"
              title="Raise front 0.25 inches"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Rear Ride Height */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-mono">
            <div className="flex items-center gap-2">
              <span className="font-bold uppercase tracking-wider text-shop-highlight">
                Rear Suspension Height
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-shop-panel text-shop-muted border border-shop-border">
                {getStanceCategory(stance.rearHeightOffsetInches)}
              </span>
            </div>
            <span className="text-safety-amber font-bold">
              {stance.rearHeightOffsetInches >= 0 ? '+' : ''}
              {stance.rearHeightOffsetInches.toFixed(2)}&quot;
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => nudge('rearHeightOffsetInches', -0.25)}
              className="min-h-[44px] min-w-[44px] rounded bg-shop-panel hover:bg-shop-well text-shop-highlight border border-shop-border flex items-center justify-center cursor-pointer active:translate-y-0.5 transition-all duration-100"
              title="Lower rear 0.25 inches"
            >
              <Minus className="w-4 h-4" />
            </button>

            <div className="flex-1 relative flex items-center h-11">
              <input
                type="range"
                min="-4.0"
                max="2.0"
                step="0.25"
                value={stance.rearHeightOffsetInches}
                onChange={(e) =>
                  onChangeStance({
                    rearHeightOffsetInches: parseFloat(e.target.value),
                  })
                }
                className="w-full accent-safety-amber h-2 bg-shop-panel rounded cursor-pointer"
              />
            </div>

            <button
              onClick={() => nudge('rearHeightOffsetInches', 0.25)}
              className="min-h-[44px] min-w-[44px] rounded bg-shop-panel hover:bg-shop-well text-shop-highlight border border-shop-border flex items-center justify-center cursor-pointer active:translate-y-0.5 transition-all duration-100"
              title="Raise rear 0.25 inches"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 3. Quick Stance Calibration Presets */}
      <div className="space-y-2">
        <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
          Quick Stance Presets
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {quickPresets.map((preset) => (
            <button
              key={preset.label}
              onClick={() =>
                onChangeStance({
                  frontHeightOffsetInches: preset.front,
                  rearHeightOffsetInches: preset.rear,
                })
              }
              className="min-h-[48px] p-2 rounded bg-shop-dark border border-shop-border hover:bg-shop-well hover:border-shop-steel text-left cursor-pointer transition-all duration-100"
            >
              <div className="text-xs font-bold text-shop-highlight uppercase truncate">
                {preset.label}
              </div>
              <div className="text-[10px] font-mono text-safety-amber font-semibold mt-0.5">
                F: {preset.front >= 0 ? '+' : ''}
                {preset.front.toFixed(1)}&quot; | R: {preset.rear >= 0 ? '+' : ''}
                {preset.rear.toFixed(1)}&quot;
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
